import { useState } from 'react';
import { MapPin } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { DisciplineOptions, getReferralNotificationPreviewOptions, type AddressViewModel } from '@generated';
import { useAzureMapsAutocomplete } from '~/shared/maps/useAzureMapsAutocomplete';
import { AzureMapComponent } from '~/shared/maps/AzureMapComponent';
import type { RouteWaypoint } from '~/shared/maps/useAzureMapsRoute';
import { SearchInput } from '~/shared/forms/controls/base-controls/text-controls/SearchInput';
import { FieldLabelWithHtml } from '~/shared/forms/controls/base-controls/FieldLabelWithHtml';
import { CheckboxGroupField } from '~/shared/forms/controls/base-fields/CheckboxGroupField';
import { Checkbox } from '~/shared/forms/controls/base-controls/Checkbox';

const zipCodePattern = /^\d{5}$/;

function milesBetween(from: RouteWaypoint, to: RouteWaypoint) {
	const toRadians = (x: number) => (x * Math.PI) / 180;
	const earthRadiusMiles = 3958.8;
	const latitudeDelta = toRadians(to.latitude - from.latitude);
	const longitudeDelta = toRadians(to.longitude - from.longitude);
	const a =
		Math.sin(latitudeDelta / 2) ** 2 + Math.cos(toRadians(from.latitude)) * Math.cos(toRadians(to.latitude)) * Math.sin(longitudeDelta / 2) ** 2;
	return 2 * earthRadiusMiles * Math.asin(Math.sqrt(a));
}

export function ReferralNotificationPreview() {
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedAddress, setSelectedAddress] = useState<AddressViewModel | null>(null);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [disciplines, setDisciplines] = useState<string[]>([]);

	const trimmedSearch = searchTerm.trim();
	const isZipCodeSearch = zipCodePattern.test(trimmedSearch);
	const zipCode = isZipCodeSearch ? trimmedSearch : selectedAddress?.zipCode ?? '';

	const { data: suggestions } = useAzureMapsAutocomplete(isZipCodeSearch || selectedAddress ? '' : searchTerm);
	const { data, isFetching } = useQuery(getReferralNotificationPreviewOptions(zipCode, disciplines.join(',')));

	const origin: RouteWaypoint | null =
		selectedAddress && !isZipCodeSearch
			? { latitude: selectedAddress.latitude, longitude: selectedAddress.longitude, label: selectedAddress.formattedAddress }
			: typeof data?.zipCodeLatitude === 'number' && typeof data.zipCodeLongitude === 'number'
			? { latitude: data.zipCodeLatitude, longitude: data.zipCodeLongitude, label: zipCode }
			: null;

	const users = (data?.users ?? [])
		.map(x => ({
			...x,
			location:
				typeof x.latitude === 'number' && typeof x.longitude === 'number'
					? { latitude: x.latitude, longitude: x.longitude, label: x.name }
					: null
		}))
		.map(x => ({ ...x, distanceMiles: origin && x.location ? milesBetween(origin, x.location) : null }))
		.sort((x, y) => (x.distanceMiles ?? Infinity) - (y.distanceMiles ?? Infinity) || x.name.localeCompare(y.name));

	const waypoints = users.flatMap(x => (x.location ? [x.location] : []));

	const onSearchChange = (value: string) => {
		setSearchTerm(value);
		setSelectedAddress(null);
		setShowSuggestions(true);
	};

	const onAddressSelect = (address: AddressViewModel) => {
		setSelectedAddress(address);
		setSearchTerm(address.formattedAddress);
		setShowSuggestions(false);
	};

	const hasCriteria = Boolean(zipCode) && disciplines.length > 0;

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-col gap-4 lg:flex-row lg:items-start">
				<div
					className="relative lg:w-1/2"
					onFocus={() => setShowSuggestions(true)}
					onBlur={x => {
						if (!x.currentTarget.contains(x.relatedTarget as Node)) {
							setShowSuggestions(false);
						}
					}}
				>
					<FieldLabelWithHtml className="mb-2" label="Zip Code or Address" />
					<SearchInput
						autoComplete="no-autofill"
						value={searchTerm}
						onChange={onSearchChange}
						placeholder="Enter a zip code or search for an address..."
						onClear={searchTerm ? () => onSearchChange('') : undefined}
					/>
					{showSuggestions && suggestions && suggestions.length > 0 && (
						<div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
							{suggestions.map(x => (
								<button
									key={x.mapIntegrationId}
									type="button"
									onMouseDown={y => y.preventDefault()}
									onClick={() => onAddressSelect(x)}
									className="flex w-full items-start gap-3 border-b border-gray-100 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-gray-50"
								>
									<MapPin className="mt-0.5 size-5 shrink-0 text-gray-400" />
									<div className="text-sm font-medium text-gray-900">{x.formattedAddress}</div>
								</button>
							))}
						</div>
					)}
				</div>
				<CheckboxGroupField
					label="Disciplines"
					orientation="horizontal"
					className="flex-wrap gap-x-2 gap-y-0"
					value={disciplines}
					onChange={setDisciplines}
				>
					{DisciplineOptions.map(x => (
						<Checkbox key={x.id} value={x.id.toString()} label={x.abbreviation} size="small" />
					))}
				</CheckboxGroupField>
			</div>

			<div className="flex flex-col gap-4 lg:flex-row">
				<div className="h-[600px] lg:flex-1">
					<AzureMapComponent startingPoint={origin} waypoints={waypoints} className="h-full" />
				</div>
				<div className="flex max-h-[600px] flex-col rounded-lg border border-gray-200 lg:w-96">
					<div className="border-b border-gray-200 px-4 py-3 text-sm font-medium text-gray-900">
						{hasCriteria
							? `${users.length} user${users.length === 1 ? '' : 's'} would be notified`
							: 'Enter a zip code or address and select disciplines'}
						{isFetching && <span className="ml-2 text-gray-500">Loading...</span>}
					</div>
					<ul className="divide-y divide-gray-100 overflow-y-auto">
						{users.map(x => (
							<li key={x.userId} className="flex items-center gap-3 px-4 py-2 text-sm">
								<span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">
									{x.location ? waypoints.indexOf(x.location) + 1 : '–'}
								</span>
								<div className="min-w-0 flex-1">
									<div className="truncate font-medium text-gray-900">
										{x.name}
										{x.clinicalTypeAbbreviation && <span className="ml-1 text-gray-500">({x.clinicalTypeAbbreviation})</span>}
									</div>
									{x.homeZipCode && <div className="text-xs text-gray-500">Home zip {x.homeZipCode}</div>}
								</div>
								<div className="shrink-0 text-gray-700">
									{x.distanceMiles !== null ? `${x.distanceMiles.toFixed(1)} mi` : x.location ? '–' : 'No address'}
								</div>
							</li>
						))}
					</ul>
				</div>
			</div>
		</div>
	);
}
